import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { parsePhoneNumber } from 'libphonenumber-js'
import type { ChangeEvent } from 'react'
import { Form, data, redirect, useSearchParams } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary'
import {
	AuthAlt,
	AuthConsent,
	AuthErrors,
	AuthField,
	AuthFieldRow,
	AuthHeading,
	AuthPhoneField,
	AuthShell,
	AuthSubmit,
	SocialButtons,
} from '#app/components/site/auth'
import { getAuthConfig } from '#app/utils/auth-config.server'
import { getPasswordHash, requireAnonymous } from '#app/utils/auth.server'
import { capitalizeWords } from '#app/utils/capitalize'
import { db } from '#app/utils/db.server'
import { requireNotSpam } from '#app/utils/honeypot.server'
import { useIsPending } from '#app/utils/misc'
import { withSearchParams } from '#app/utils/site/auth-ui'
import { siteContent } from '#app/utils/site/content'
import { siteLinks } from '#app/utils/site/site-links'
import { EmailSchema, PasswordSchema } from '#app/utils/user-validation'
import { verifySessionStorage } from '#app/utils/verification.server'
import type { Route } from './+types/tenant.signup'
import {
	SIGNUP_KEYS,
	readSignupResume,
	startSignupCode,
} from './verify-account.server'

interface SEOHandle {
	getSitemapEntries: () => null
}

export const handle = {
	getSitemapEntries: () => null,
} satisfies SEOHandle

export const links = siteLinks()

const PhoneSchema = z
	.string({ required_error: 'Phone number is required' })
	.min(1, { message: 'Phone number is required' })
	.refine(
		(phone) => {
			if (!phone || phone.trim() === '') {
				return false
			}
			try {
				const parsed = parsePhoneNumber(phone)
				return parsed?.isValid() ?? false
			} catch {
				try {
					const parsed = parsePhoneNumber(phone, 'CA')
					return parsed?.isValid() ?? false
				} catch {
					return false
				}
			}
		},
		{
			message: 'Please enter a valid phone number',
		},
	)

const FirstNameSchema = z
	.string({ required_error: 'First name is required' })
	.min(1, { message: 'First name is required' })
	.max(50, { message: 'First name is too long' })
	// Title-case so the stored name has a capital first letter regardless of typing.
	.transform((value) => capitalizeWords(value))

const LastNameSchema = z
	.string({ required_error: 'Last name is required' })
	.min(1, { message: 'Last name is required' })
	.max(50, { message: 'Last name is too long' })
	.transform((value) => capitalizeWords(value))

const SignupSchema = z
	.object({
		firstname: FirstNameSchema,
		lastname: LastNameSchema,
		email: EmailSchema,
		phone: PhoneSchema,
		password: PasswordSchema,
		confirmPassword: PasswordSchema,
	})
	.superRefine(({ confirmPassword, password }, ctx) => {
		if (confirmPassword !== password) {
			ctx.addIssue({
				path: ['confirmPassword'],
				code: 'custom',
				message: 'The passwords must match',
			})
		}
	})

export async function loader({ request }: Route.LoaderArgs) {
	await requireAnonymous(request)
	const authConfig = getAuthConfig('tenant')
	const url = new URL(request.url)
	const invitation = url.searchParams.get('invitation')
	// "Back To Sign Up" from the code screen: fill the form again.
	const resume =
		url.searchParams.get('resume') === '1'
			? await readSignupResume(request)
			: null
	return { authConfig, invitation, resume }
}

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	await requireNotSpam(formData)

	const submission = await parseWithZod(formData, {
		schema: SignupSchema.superRefine(async (data, ctx) => {
			const existingUser = await db.query.user.findFirst({
				columns: { id: true },
				where: (user, { eq }) => eq(user.email, data.email),
			})
			if (existingUser) {
				ctx.addIssue({
					path: ['email'],
					code: z.ZodIssueCode.custom,
					message: 'A user already exists with this email',
				})
				return
			}
		}),
		async: true,
	})

	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{
				status: 500,
			},
		)
	}
	const { email, firstname, lastname, phone, password } = submission.value
	// What the form had waits in the verify session until the code is right.
	const session = await verifySessionStorage.getSession()
	// Only the hash waits in the cookie, never the password itself.
	session.set(SIGNUP_KEYS.passwordHash, await getPasswordHash(password))
	session.set(SIGNUP_KEYS.firstName, firstname)
	session.set(SIGNUP_KEYS.lastName, lastname)
	session.set(SIGNUP_KEYS.phone, phone)
	session.set(SIGNUP_KEYS.userType, 'tenant')
	const invitation = formData.get('invitation')?.toString()
	if (invitation) session.set(SIGNUP_KEYS.invitation, invitation)
	const redirectTo = formData.get('redirectTo')?.toString()
	if (redirectTo) session.set(SIGNUP_KEYS.redirectTo, redirectTo)
	return startSignupCode({ request, session, email })
}

export const meta: Route.MetaFunction = () => {
	return [{ title: 'Sign Up | TrueTenant - Tenant' }]
}

export default function TenantSignupRoute({
	actionData,
	loaderData,
}: Route.ComponentProps) {
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')
	const invitation = loaderData.invitation || searchParams.get('invitation')
	const authConfig = loaderData.authConfig

	// Build login link preserving redirectTo and invitation params
	const loginLink = withSearchParams(authConfig.loginPath, {
		redirectTo,
		invitation,
	})

	const [form, fields] = useForm<z.input<typeof SignupSchema>>({
		id: 'signup-form',
		constraint: getZodConstraint(SignupSchema),
		defaultValue: loaderData.resume ?? undefined,
		lastResult: actionData?.result,
		onValidate({ formData }) {
			const result = parseWithZod(formData, { schema: SignupSchema })
			return result
		},
		shouldRevalidate: 'onBlur',
	})

	// Title-case as the user types (caret preserved: the length is unchanged),
	// matching the screening name fields.
	const titleCaseAsTyped = (event: ChangeEvent<HTMLInputElement>) => {
		const el = event.currentTarget
		const pos = el.selectionStart
		el.value = capitalizeWords(el.value)
		if (pos !== null) el.setSelectionRange(pos, pos)
	}

	const copy = siteContent.auth
	const labels = copy.fields

	return (
		<AuthShell audience="tenant">
			<Form method="POST" {...getFormProps(form)}>
				<HoneypotInputs />
				{invitation && (
					<input type="hidden" name="invitation" value={invitation} />
				)}
				{redirectTo && (
					<input type="hidden" name="redirectTo" value={redirectTo} />
				)}
				<AuthHeading eyebrow={copy.signup.eyebrow} title={copy.role.tenant} />
				<AuthFieldRow>
					<AuthField
						label={labels.firstName}
						hint={siteContent.auth.hints.firstName}
						autoComplete="given-name"
						inputProps={{
							...getInputProps(fields.firstname, { type: 'text' }),
							onChange: titleCaseAsTyped,
						}}
						errors={fields.firstname.errors}
					/>
					<AuthField
						label={labels.lastName}
						hint={siteContent.auth.hints.lastName}
						autoComplete="family-name"
						inputProps={{
							...getInputProps(fields.lastname, { type: 'text' }),
							onChange: titleCaseAsTyped,
						}}
						errors={fields.lastname.errors}
					/>
				</AuthFieldRow>
				<AuthField
					label={labels.email}
					hint={siteContent.auth.hints.email}
					type="email"
					autoComplete="email"
					inputProps={getInputProps(fields.email, { type: 'email' })}
					errors={fields.email.errors}
				/>
				<AuthPhoneField
					label={labels.phone}
					inputProps={getInputProps(fields.phone, { type: 'text' })}
					errors={fields.phone.errors}
				/>
				<AuthField
					label={labels.password}
					hint={siteContent.auth.hints.password}
					type="password"
					autoComplete="new-password"
					inputProps={getInputProps(fields.password, { type: 'password' })}
					errors={fields.password.errors}
				/>
				<AuthField
					label={labels.confirmPassword}
					hint={siteContent.auth.hints.confirmPassword}
					type="password"
					autoComplete="new-password"
					inputProps={getInputProps(fields.confirmPassword, {
						type: 'password',
					})}
					errors={fields.confirmPassword.errors}
				/>
				<AuthErrors errors={form.errors} id={form.errorId} />
				<AuthSubmit pending={isPending}>{copy.signup.submit}</AuthSubmit>
			</Form>
			<SocialButtons
				type="Continue"
				redirectTo={redirectTo}
				userType="tenant"
				invitation={invitation}
			/>
			<AuthAlt
				text={copy.signup.alt}
				link={{ label: copy.signup.altLink, to: loginLink }}
			/>
			<AuthConsent kind="signup" />
		</AuthShell>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
